import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Fuel, TrendingUp, Calendar, BarChart3, Edit, Trash2, LogOut, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import { DateTimeInput } from '@/components/ui/datetime-input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RefuelingPage() {
  const navigate = useNavigate();
  const [refuelings, setRefuelings] = useState([]);
  const [buses, setBuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [filters, setFilters] = useState({
    bus_id: 'all',
    user_id: 'all',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [editingRefueling, setEditingRefueling] = useState(null);
  const [editForm, setEditForm] = useState({
    date: '',
    bus_id: '',
    user_id: '',
    previous_mileage: '',
    current_mileage: '',
    liters: ''
  });
  const [user, setUser] = useState(null);

  // Helper function to get consumption color based on 4-threshold system
  const getConsumptionColor = (consumption, busId) => {
    const bus = buses.find(b => b.id === busId);
    const redLow = bus?.red_threshold_low || 20;
    const yellowLow = bus?.yellow_threshold_low || 25;
    const yellowHigh = bus?.yellow_threshold_high || 30;
    const redHigh = bus?.red_threshold_high || 35;

    if (consumption < redLow || consumption > redHigh) return 'text-red-600 font-bold';
    if (consumption >= yellowLow && consumption <= yellowHigh) return 'text-green-600 font-semibold';
    return 'text-yellow-600 font-semibold';
  };

  useEffect(() => {
    fetchInitialData();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchRefuelings();
      fetchStats();
    }
  }, [filters, loading]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [busesRes, usersRes] = await Promise.all([
        axios.get(`${API}/buses`, { headers }),
        axios.get(`${API}/users`, { headers })
      ]);

      setBuses(busesRes.data);
      // Фильтруем по is_driver или role === 'driver'
      setUsers(usersRes.data.filter(u => u.is_driver || u.role === 'driver'));
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const fetchRefuelings = async () => {
    try {
      const params = new URLSearchParams();
      
      if (filters.bus_id && filters.bus_id !== 'all') params.append('bus_id', filters.bus_id);
      if (filters.user_id && filters.user_id !== 'all') params.append('user_id', filters.user_id);
      if (filters.month) params.append('month', filters.month);
      if (filters.year) params.append('year', filters.year);

      const response = await axios.get(`${API}/refuelings?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setRefuelings(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки истории');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.bus_id && filters.bus_id !== 'all') params.append('bus_id', filters.bus_id);
      if (filters.user_id && filters.user_id !== 'all') params.append('user_id', filters.user_id);
      params.append('month', filters.month);
      params.append('year', filters.year);

      const response = await axios.get(`${API}/refuelings/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleEditClick = (refueling) => {
    setEditingRefueling(refueling);
    const dateObj = new Date(refueling.date);
    const localDate = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000);
    const formattedDate = localDate.toISOString().slice(0, 16);
    
    setEditForm({
      date: formattedDate,
      bus_id: refueling.bus_id,
      user_id: refueling.user_id,
      previous_mileage: refueling.previous_mileage,
      current_mileage: refueling.current_mileage,
      liters: refueling.liters
    });
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    const prevMileage = parseInt(editForm.previous_mileage) || 0;
    
    let currMileage = null;
    if (editForm.current_mileage !== '' && editForm.current_mileage !== null && editForm.current_mileage !== undefined) {
      const parsed = parseInt(editForm.current_mileage);
      if (!isNaN(parsed) && parsed > 0) {
        currMileage = parsed;
      }
    }
    
    if (currMileage !== null && prevMileage >= currMileage) {
      toast.error('Начальный пробег должен быть меньше конечного');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        date: new Date(editForm.date).toISOString(),
        bus_id: editForm.bus_id,
        user_id: editForm.user_id,
        previous_mileage: prevMileage,
        liters: parseFloat(editForm.liters)
      };
      
      if (currMileage !== null && currMileage > 0) {
        payload.current_mileage = currMileage;
      } else {
        payload.current_mileage = 0;
      }
      
      await axios.put(
        `${API}/refuelings/${editingRefueling.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Заправка обновлена');
      setEditDialog(false);
      fetchRefuelings();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const handleDelete = async (refuelingId) => {
    if (!window.confirm('Удалить эту заправку?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/refuelings/${refuelingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Заправка удалена');
      fetchRefuelings();
      fetchStats();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const prepareChartData = () => {
    const grouped = {};
    const validRefuelings = refuelings.filter(ref => ref.liters > 0 && ref.consumption > 0);
    
    validRefuelings.forEach(ref => {
      const date = formatDate(ref.date);
      if (!grouped[date]) {
        grouped[date] = { date, consumption: 0, count: 0 };
      }
      grouped[date].consumption += ref.consumption;
      grouped[date].count += 1;
    });

    return Object.values(grouped).map(item => ({
      date: item.date,
      consumption: parseFloat((item.consumption / item.count).toFixed(2))
    })).sort((a, b) => a.date.localeCompare(b.date));
  };

  const months = [
    { value: 1, label: 'Январь' },
    { value: 2, label: 'Февраль' },
    { value: 3, label: 'Март' },
    { value: 4, label: 'Апрель' },
    { value: 5, label: 'Май' },
    { value: 6, label: 'Июнь' },
    { value: 7, label: 'Июль' },
    { value: 8, label: 'Август' },
    { value: 9, label: 'Сентябрь' },
    { value: 10, label: 'Октябрь' },
    { value: 11, label: 'Ноябрь' },
    { value: 12, label: 'Декабрь' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-orange-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-orange-100">
      {/* Modern Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Brand */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Fuel className="w-5 h-5 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-white">Контроль топлива</h1>
                <p className="text-xs text-slate-400">История заправок и расход</p>
              </div>
            </div>


            {/* Right Section */}
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-sm text-slate-400">
                {user?.username}
              </span>
              <Button 
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Выход</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="bg-white shadow-lg border-0 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-blue-400 to-blue-600" />
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Заправок</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.refueling_count}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg border-0 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-green-400 to-green-600" />
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Всего литров</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_liters}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Fuel className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg border-0 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-purple-400 to-purple-600" />
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Пробег (км)</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_distance.toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg border-0 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-orange-400 to-orange-600" />
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Средний расход</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.average_consumption} л/100км</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Chart */}
        {refuelings.length > 0 && (
          <Card className="mb-6 bg-white/80 backdrop-blur shadow-lg border-0 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-orange-400 to-red-500" />
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">График расхода топлива</CardTitle>
              <CardDescription>Средний расход по дням</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={prepareChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" label={{ value: 'л/100км', angle: -90, position: 'insideLeft', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff'
                    }} 
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="consumption"
                    stroke="#f97316"
                    strokeWidth={3}
                    name="Расход (л/100км)"
                    dot={{ fill: '#f97316', strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: '#ea580c' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6 bg-white/80 backdrop-blur shadow-lg border-0 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-400 to-indigo-500" />
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600" />
              Фильтры
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Автобус</label>
                <Select
                  value={filters.bus_id}
                  onValueChange={(value) => setFilters({ ...filters, bus_id: value })}
                >
                  <SelectTrigger className="h-9 bg-slate-50">
                    <SelectValue placeholder="Все автобусы" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все автобусы</SelectItem>
                    {buses.map((bus) => (
                      <SelectItem key={bus.id} value={bus.id}>
                        {bus.license_plate}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Водитель</label>
                <Select
                  value={filters.user_id}
                  onValueChange={(value) => setFilters({ ...filters, user_id: value })}
                >
                  <SelectTrigger className="h-9 bg-slate-50">
                    <SelectValue placeholder="Все водители" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все водители</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {(user.first_name || user.last_name) 
                          ? `${user.first_name || ''} ${user.last_name || ''}`.trim() 
                          : user.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Месяц</label>
                <Select
                  value={filters.month.toString()}
                  onValueChange={(value) => setFilters({ ...filters, month: parseInt(value) })}
                >
                  <SelectTrigger className="h-9 bg-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Год</label>
                <Select
                  value={filters.year.toString()}
                  onValueChange={(value) => setFilters({ ...filters, year: parseInt(value) })}
                >
                  <SelectTrigger className="h-9 bg-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white/80 backdrop-blur shadow-lg border-0 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-green-400 to-emerald-500" />
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">История заправок</CardTitle>
            <CardDescription>{refuelings.length} записей</CardDescription>
          </CardHeader>
          <CardContent>
            {refuelings.length === 0 ? (
              <div className="text-center py-12">
                <Fuel className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Нет данных</h3>
                <p className="text-sm text-gray-500">За выбранный период нет записей</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Дата</TableHead>
                      <TableHead className="text-xs">Водитель</TableHead>
                      <TableHead className="text-xs">Автобус</TableHead>
                      <TableHead className="text-xs text-right">Начало</TableHead>
                      <TableHead className="text-xs text-right">Конец</TableHead>
                      <TableHead className="text-xs text-right">Пробег</TableHead>
                      <TableHead className="text-xs text-right">Литры</TableHead>
                      <TableHead className="text-xs text-right">Расход</TableHead>
                      <TableHead className="text-xs text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {refuelings.map((ref) => {
                      const distance = ref.current_mileage - ref.previous_mileage;
                      const hasNoConsumption = ref.consumption === 0 || distance === 0;
                      
                      return (
                        <TableRow key={ref.id} className={hasNoConsumption ? 'bg-yellow-50/50' : 'hover:bg-slate-50'}>
                          <TableCell className="text-xs">
                            {formatDateTime(ref.date)}
                          </TableCell>
                          <TableCell className="text-xs font-medium">{ref.username}</TableCell>
                          <TableCell className="text-xs">{ref.bus_name}</TableCell>
                          <TableCell className="text-xs text-right">
                            {ref.previous_mileage.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-xs text-right">
                            {hasNoConsumption ? '—' : ref.current_mileage.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-xs text-right">
                            {hasNoConsumption ? '—' : distance.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-xs text-right font-medium">{ref.liters}</TableCell>
                          <TableCell className="text-xs text-right">
                            {hasNoConsumption ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <span className={getConsumptionColor(ref.consumption, ref.bus_id)}>
                                {ref.consumption}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditClick(ref)}
                                className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(ref.id)}
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Редактировать заправку</DialogTitle>
            <DialogDescription>
              Изменение пробега автоматически обновит связанные заправки
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Дата и время</Label>
              <DateTimeInput
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                placeholder="гггг/мм/дд ЧЧ:ММ"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Автобус</Label>
              <Select
                value={editForm.bus_id}
                onValueChange={(value) => setEditForm({ ...editForm, bus_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Выберите автобус" />
                </SelectTrigger>
                <SelectContent>
                  {buses.map((bus) => (
                    <SelectItem key={bus.id} value={bus.id}>
                      {bus.license_plate} - {bus.brand} {bus.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Водитель</Label>
              <Select
                value={editForm.user_id}
                onValueChange={(value) => setEditForm({ ...editForm, user_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Выберите водителя" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {(user.first_name || user.last_name) 
                        ? `${user.first_name || ''} ${user.last_name || ''}`.trim() 
                        : user.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Начальный пробег *</Label>
                <Input
                  type="number"
                  value={editForm.previous_mileage}
                  onChange={(e) => setEditForm({ ...editForm, previous_mileage: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Конечный пробег</Label>
                <Input
                  type="number"
                  value={editForm.current_mileage === 0 || !editForm.current_mileage ? '' : editForm.current_mileage}
                  onChange={(e) => setEditForm({ ...editForm, current_mileage: e.target.value === '' ? null : e.target.value })}
                  placeholder="Необязательно"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Литры</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.liters}
                onChange={(e) => setEditForm({ ...editForm, liters: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setEditDialog(false)}>
                Отмена
              </Button>
              <Button size="sm" onClick={handleEditSave} className="bg-orange-500 hover:bg-orange-600">
                Сохранить
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
